import { z } from "zod";
import { ManagedPromptType, PromptType } from '../context/SavedPromptsContext';
import { UserFacingError } from './errors';
import { getEnv } from './envManager';
import type { GroupableTurnArtifacts } from "@common/components/chat/groupAssistantSteps";
import type { ConversationArtifact, ConversationArtifactRow } from "@common/schemas/conversation-artifact";
import type { OrgUser } from "@common/components/share/types";
import { clickDownloadLink, downloadStoredObject, PresignedDownloadResponseSchema, type FetchJson } from "@common/utils/presignedDownload";
import { ResourceAccessSchema, type ResourceAccess } from "@common/utils/resourceAccess";
import { safeFetch } from './safe-fetch';

const attachmentFileSchema = z.object({
  id: z.string(),
  fileName: z.string(),
  inFocus: z.boolean(),
  conversationId: z.string().optional(),
  createdAt: z.string().optional(),
  storageUrl: z.string().nullish(),
  tokenCount: z.number().nullish(),
  fileBase64: z.string().optional(),
}).passthrough();

export type AttachmentFile = z.infer<typeof attachmentFileSchema>;

export interface ShareGrant {
  id: string;
  granteeUserId: string;
  user: OrgUser | null;
}

export interface SharesResponse {
  owner?: OrgUser | null;
  grants: ShareGrant[];
}

// Back-compat aliases — prompts referenced these names before sharing was
// generalized across resource types.
export type PromptShareGrant = ShareGrant;
export type PromptSharesResponse = SharesResponse;

export type PlaybookStatus = "initializing" | "draft" | "active" | "archived" | "deleted" | "error";

export interface Rule {
  id?: string;
  name: string;
  description: string;
  category?: string;
  importance?: string;
  type?: string;
  tags?: string[];
  isManagedRule?: boolean;
  ruleType: string;
  rule: string;
  remedyType: string;
  remedy: string;
  remedyModel?: string;
  comment: string;
  playbookId?: string;
  remedyName?: string | null;
  remedyDescription?: string | null;
  alternativeRemedyName?: string[] | null;
  alternativeRemedyDescription?: string[] | null;
  alternativeRemedy?: string[] | null;
  alternativeRemedyContext?: string[] | null;
  alternativeRemedyModel?: string[] | null;
  alternativeRemedyTarget?: string[] | null;
}

export interface PlaybookCategory {
  name: string;
  rules: Rule[];
}

export interface Playbook {
  id: string;
  propelAuthOrgID: string;
  propelAuthUserID: string | null;
  name: string | null;
  description: string | null;
  ourSide: string | null;
  relevantAgreement: string | null;
  playbookID: string | null;
  disabledRules: string[];
  matchingUserRulesIds: string[];
  matchingManagedRulesIds: string[];
  createdAt: Date;
  googleSheetID: string | null;
  status: PlaybookStatus;
  error: string | null;
  precedentsDocuments: string[];
  guidelinesDocuments: string[];
  addingPrecedentsDocuments: string[];
  addingGuidelineDocument: string[];
  importedPrecedentsReports: string[];
  importedGuidelinesReports: string[];
  importingFirstPartyGuidelines: string | null;
  importingFirstPartyStandardForm: string | null;
  isGlobalPlaybook: boolean;
  reviewTrackedChanges?: boolean;
  issueCount?: number;
  bySection?: PlaybookCategory[];
}

export interface NewFeedbackItem {
  isPositive: boolean;
  tags: string[];
  details: string;
  reviewId: string;
  userRuleId: string;
  managedRuleGoogleSheetId: string;
  managedRuleId: string;
}

export interface FeedbackItem extends NewFeedbackItem {
  id: string;
  propelAuthUserID: string;
  propelAuthOrgID: string;
  createdAt: Date;
}

export interface Review {
  id: string;
  userPlaybookID?: string;
  userPlaybookIDs?: string[];
  propelAuthOrgID: string;
  status: string;
  arguments: any;
  validationMessage?: string;
  concededRules?: string[];
  respondedRules?: string[];
  author?: string;
  createdAt?: string;
}

export enum Role {
  USER = "user",
  ASSISTANT = "assistant",
  SYSTEM = "system"
}

export interface Message {
  role: Role;
  content: string;
  conversationId?: string;
}

export interface ChatRequestBody {
  messages: Message[];
}

const chatConversationSchema = z.object({
  id: z.string(),
  name: z.string(),
  createdAt: z.string(),
  project: z.object({ id: z.string(), name: z.string() }).nullish(),
  lastMessageAt: z.string().nullish(),
  agentInstructions: z.string().nullish(),
}).passthrough();

export type ChatConversation = z.infer<typeof chatConversationSchema>;

const conversationMessageSchema = z.object({
  id: z.string(),
  content: z.string(),
  turnType: z.enum(["user", "assistant"]),
  createdAt: z.string().nullish(),
  completedAt: z.string().nullish(),
  turnArtifacts: z.record(z.string(), z.unknown()).nullish(),
}).passthrough();

function toConversationMessage(row: z.infer<typeof conversationMessageSchema>): ConversationMessage {
  return {
    ...row,
    createdAt: row.createdAt ?? "",
    completedAt: row.completedAt ?? null,
    turnArtifacts: (row.turnArtifacts ?? {}) as GroupableTurnArtifacts,
  };
}

export interface ClauseSharing {
  sharedUserCount: number;
  hasDirectUserGrants: boolean;
  hasAnyShares: boolean;
}

export interface Clause {
  id: string;
  propelAuthUserID: string;
  createdAt: Date;
  propelAuthOrgID: string;
  contentText: string;
  documentUrl?: string;
  documentName?: string;
  tags?: string[];
  // Present only when the request supplied "similar to my selection" text.
  similarityScore?: number;
  access?: ResourceAccess;
  sharing?: ClauseSharing;
  ownerName?: string;
}

export interface SearchClausesParams {
  mode?: "all" | "mine" | "shared";
  q?: string;
  tags?: string[];
  similarTo?: string;
  sort?: "relevance" | "recent";
  offset?: number;
  limit?: number;
}

export interface SearchClausesResponse {
  clauses: Clause[];
  total: number;
  limit: number;
  offset: number;
}

export interface ConversationMessage {
  id: string,
  propelAuthUserID?: string,
  createdAt: string,
  completedAt?: string | null,
  propelAuthOrgID?: string,
  conversationId?: string,
  content: string,
  turnType: "user" | "assistant",
  turnArtifacts: GroupableTurnArtifacts
}

export interface UserPrompt {
  id: string;
  propelAuthUserID: string;
  propelAuthOrgID: string;
  name: string;
  content: string;
  description?: string | null;
  type: PromptType;
  path: string;
  createdAt: string;
  access?: ResourceAccess;
  ownerName?: string;
}

export interface NewUserPrompt {
  name: string;
  content: string;
  description?: string | null;
  type: PromptType;
  path?: string;
}

// A document attached to a saved prompt. Mirrors the row returned by the
// /api/prompts/user/{id}/attachments endpoints.
const promptAttachmentSchema = z.object({
  id: z.string(),
  promptId: z.string(),
  propelAuthOrgID: z.string(),
  fileName: z.string(),
  storageUrl: z.string(),
  tokenCount: z.number().nullable(),
  createdAt: z.string(),
}).passthrough();

export type PromptAttachment = z.infer<typeof promptAttachmentSchema>;

export interface NewPromptAttachment {
  fileName: string;
  fileBase64: string;
}

export interface ManagedPrompt {
  id: string;
  name: string;
  content: string;
  description?: string | null;
  type: PromptType;
  path: string;
  createdAt: string;
}

const managedPromptCatalogEntrySchema = z.object({
  id: z.string(),
  name: z.string(),
  content: z.string(),
  description: z.string().nullish(),
  type: z.enum(["prompt", "skill"]),
  path: z.string(),
  createdAt: z.string(),
}).passthrough();

const PROMPT_TYPE_BY_MANAGED_TYPE: Record<ManagedPromptType, PromptType> = {
  prompt: "chat",
  skill: "skill",
};

export const UserFileSchema = z.object({
  id: z.string(),
  propelAuthUserID: z.string(),
  propelAuthOrgID: z.string(),
  name: z.string(),
  fileUrl: z.string(),
  path: z.string(),
  createdAt: z.string(),
  access: ResourceAccessSchema.optional(),
});

export type UserFile = z.infer<typeof UserFileSchema>;

export interface NewUserFile {
  name: string;
  fileBase64: string;
}

export interface ManagedFile {
  id: string;
  name: string;
  fileUrl: string;
  path: string;
  createdAt: string;
}


export const fetchPlaybooks = async (authHeader: string): Promise<Playbook[]> => {
  const apiUrl = `${getEnv("WEBAPP_API_BASE_URL")}/api/playbooks`;
  const response = await safeFetch(apiUrl, {
    headers: {
      Authorization: authHeader,
    },
  });
  return await response.json();
};

export const fetchPlaybook = async (authHeader: string, id: string): Promise<Playbook> => {
  const apiUrl = `${getEnv("WEBAPP_API_BASE_URL")}/api/playbooks/${id}`;
  const response = await safeFetch(apiUrl, {
    headers: {
      Authorization: authHeader,
    },
  });
  return await response.json();
};

const createdPlaybookSchema = z.object({ id: z.string() }).passthrough();

const PLAYBOOK_CREATION_RETRY_CONFIG = {
  retryStatuses: [],
  retryOnNetworkError: false,
};

export const createPlaybook = async (
  authHeader: string,
  name: string,
  description: string
): Promise<Pick<Playbook, "id">> => {
  const apiUrl = `${getEnv("WEBAPP_API_BASE_URL")}/api/playbooks`;
  const response = await safeFetch(apiUrl, {
    method: "POST",
    body: JSON.stringify({
      name,
      description,
    }),
    headers: {
      Authorization: authHeader,
      'Content-Type': 'application/json'
    },
  }, PLAYBOOK_CREATION_RETRY_CONFIG);
  if (!response.ok) throw new Error("Failed to create playbook");
  return createdPlaybookSchema.parse(await response.json());
};

export const duplicatePlaybook = async (
  authHeader: string,
  playbookId: string,
  name: string,
  description: string
): Promise<Pick<Playbook, "id">> => {
  const apiUrl = `${getEnv("WEBAPP_API_BASE_URL")}/api/playbooks/${playbookId}/duplicate`;
  const response = await safeFetch(apiUrl, {
    method: "POST",
    body: JSON.stringify({
      name,
      description,
    }),
    headers: {
      Authorization: authHeader,
      'Content-Type': 'application/json'
    },
  }, PLAYBOOK_CREATION_RETRY_CONFIG);
  if (!response.ok) throw new Error("Failed to duplicate playbook");
  return createdPlaybookSchema.parse(await response.json());
};

export const saveFeedback = async (authHeader: string, feedback: NewFeedbackItem): Promise<FeedbackItem | null> => {
  const apiUrl = `${getEnv("WEBAPP_API_BASE_URL")}/api/feedback-items`;
  try {
    const response = await safeFetch(apiUrl, {
      method: "POST",
      headers: {
        Authorization: authHeader,
      },
      body: JSON.stringify(feedback),
    });

    return response.json();
  } catch (err) {
    console.error(err);
    return null;
  }
};

export const updateFeedback = async (
  authHeader: string,
  feedbackId: string,
  feedback: NewFeedbackItem
): Promise<FeedbackItem | null> => {
  const apiUrl = `${getEnv("WEBAPP_API_BASE_URL")}/api/feedback-items/${feedbackId}`;
  try {
    const response = await safeFetch(apiUrl, {
      method: "PUT",
      headers: {
        Authorization: authHeader,
      },
      body: JSON.stringify(feedback),
    });

    return response.json();
  } catch (err) {
    console.error(err);
    return null;
  }
};

export const deleteFeedback = async (authHeader: string, feedbackId: string): Promise<void> => {
  const apiUrl = `${getEnv("WEBAPP_API_BASE_URL")}/api/feedback-items/${feedbackId}`;
  try {
    await safeFetch(apiUrl, {
      method: "DELETE",
      headers: {
        Authorization: authHeader,
      },
    });
  } catch (err) {
    console.error(err);
  }
};

export const findFeedback = async (
  authHeader: string,
  reviewId: string,
  ruleId: string
): Promise<FeedbackItem | null> => {
  if (!reviewId || !ruleId) return null;
  const apiUrl = `${getEnv("WEBAPP_API_BASE_URL")}/api/feedback-items?reviewId=${reviewId}&ruleId=${ruleId}`;
  try {
    const response = await safeFetch(apiUrl, {
      method: "GET",
      headers: {
        Authorization: authHeader,
      },
    });
    if (response.ok) return response.json();
    return null;
  } catch (err) {
    console.error(err);
    return null;
  }
};

export const createChatConversation = async (
  dioptraFetch: (url: string, options?: RequestInit) => Promise<any>,
  name: string
): Promise<ChatConversation> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  const conversation = await dioptraFetch(`${urlBase}/api/chat/conversation`, {
    method: "POST",
    body: JSON.stringify({ name })
  });
  return chatConversationSchema.parse(conversation);
};

export const getConversationDetails = async (
  dioptraFetch: (url: string, options?: RequestInit) => Promise<any>,
  conversationId: string
): Promise<ChatConversation> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  const conversation = await dioptraFetch(`${urlBase}/api/chat/conversation/${conversationId}`, {
    method: "GET"
  });
  return chatConversationSchema.parse(conversation);
};


export const getMessagesHistory = async (
  dioptraFetch: (url: string, options?: RequestInit) => Promise<any>,
  conversationId: string
): Promise<ConversationMessage[]> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  const rows = await dioptraFetch(
    `${urlBase}/api/chat/conversation/${conversationId}/messages`,
    { method: "GET" },
  );
  return z.array(conversationMessageSchema).parse(rows).map(toConversationMessage);
};

export const sendSlackMessage = async (
  authHeader: string,
  message: string
): Promise<Response> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  const response = await safeFetch(`${urlBase}/api/slack`, {
    method: "POST",
    body: JSON.stringify({ message }),
    headers: { Authorization: authHeader },
  });
  if (!response.ok) throw new Error("Failed to send chat message");

  return response;
};

export const deleteConversation = async (
  authHeader: string,
  conversationId: string
): Promise<void> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  const response = await safeFetch(`${urlBase}/api/chat/conversation/${conversationId}`, {
    method: "DELETE",
    headers: { Authorization: authHeader },
  });
  if (!response.ok) throw new Error("Failed to delete chat conversation");
};

export const getUserPrompts = async (
  authHeader: string,
  type?: PromptType
): Promise<UserPrompt[]> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  const url = type
    ? `${urlBase}/api/prompts/user?type=${type}`
    : `${urlBase}/api/prompts/user`;

  const response = await safeFetch(url, {
    method: "GET",
    headers: { Authorization: authHeader },
  });
  if (!response.ok) throw new Error("Failed to fetch user prompts");

  return await response.json();
};

export const createUserPrompt = async (
  authHeader: string,
  prompt: NewUserPrompt
): Promise<UserPrompt> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  const response = await safeFetch(`${urlBase}/api/prompts/user`, {
    method: "POST",
    body: JSON.stringify(prompt),
    headers: { Authorization: authHeader },
  });
  if (!response.ok) throw new Error("Failed to create user prompt");

  return await response.json();
};

export const updateUserPrompt = async (
  authHeader: string,
  id: string,
  prompt: NewUserPrompt
): Promise<UserPrompt> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  const response = await safeFetch(`${urlBase}/api/prompts/user/${id}`, {
    method: "PATCH",
    body: JSON.stringify(prompt),
    headers: { Authorization: authHeader },
  });
  if (!response.ok) throw new Error("Failed to update user prompt");

  return await response.json();
};

export const deleteUserPrompt = async (
  authHeader: string,
  id: string
): Promise<void> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  const response = await safeFetch(`${urlBase}/api/prompts/user/${id}`, {
    method: "DELETE",
    headers: { Authorization: authHeader },
  });
  if (!response.ok) throw new Error("Failed to delete user prompt");
};

export const getPromptAttachments = async (
  authHeader: string,
  promptId: string
): Promise<PromptAttachment[]> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  try {
    const response = await safeFetch(`${urlBase}/api/prompts/user/${promptId}/attachments`, {
      method: "GET",
      headers: { Authorization: authHeader },
    });
    const data = await response.json();
    return z.array(promptAttachmentSchema).parse(data);
  } catch (err) {
    // A managed prompt (or a prompt the caller can read but doesn't own) isn't
    // reachable via this owner-only endpoint, so it 404s. safeFetch throws
    // before returning a Response for any non-2xx status, so the 404 must be
    // caught here rather than checked on a response object. That just means
    // "no attachments to copy", not a real error.
    if ((err as { status?: number } | null)?.status === 404) return [];
    throw err;
  }
};

export const createPromptAttachment = async (
  authHeader: string,
  promptId: string,
  attachment: NewPromptAttachment
): Promise<PromptAttachment> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  const response = await safeFetch(`${urlBase}/api/prompts/user/${promptId}/attachments`, {
    method: "POST",
    body: JSON.stringify(attachment),
    headers: {
      Authorization: authHeader,
      "Content-Type": "application/json",
    },
  });
  const data = await response.json();
  return promptAttachmentSchema.parse(data);
};

export const deletePromptAttachment = async (
  authHeader: string,
  promptId: string,
  attachmentId: string
): Promise<void> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  const response = await safeFetch(`${urlBase}/api/prompts/user/${promptId}/attachments/${attachmentId}`, {
    method: "DELETE",
    headers: { Authorization: authHeader },
  });
  if (!response.ok) throw new Error("Failed to delete prompt attachment");
};

export const getManagedPrompts = async (
  authHeader: string,
  type: ManagedPromptType
): Promise<ManagedPrompt[]> => {
  const urlBase = getEnv("API_BASE_URL");
  const url = `${urlBase}/managed-prompts?type=${type}`;
  const response = await safeFetch(url, {
    method: "GET",
    headers: { Authorization: authHeader },
  });
  if (!response.ok) throw new Error("Failed to fetch managed prompts");
  const entries = z.array(managedPromptCatalogEntrySchema).parse(await response.json());
  return entries.map((entry) => ({
    ...entry,
    type: PROMPT_TYPE_BY_MANAGED_TYPE[entry.type],
  }));
};

export const publishUserPrompt = async (
  authHeader: string,
  id: string
): Promise<void> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  const response = await safeFetch(`${urlBase}/api/prompts/user/${id}/publish`, {
    method: "POST",
    headers: { Authorization: authHeader },
  });
  if (!response.ok) throw new Error("Failed to publish user prompt");
};

export const fetchPromptShares = async (authHeader: string, promptId: string): Promise<PromptSharesResponse> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  const response = await safeFetch(`${urlBase}/api/prompts/user/${promptId}/shares`, {
    headers: { Authorization: authHeader },
  });
  if (!response.ok) throw new Error("Failed to fetch prompt shares");
  return await response.json();
};

export const addPromptShare = async (authHeader: string, promptId: string, granteeUserId: string): Promise<{ id: string }> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  const response = await safeFetch(`${urlBase}/api/prompts/user/${promptId}/shares`, {
    method: "POST",
    headers: { Authorization: authHeader, "Content-Type": "application/json" },
    body: JSON.stringify({ granteeUserId }),
  });
  if (!response.ok) throw new Error("Failed to add prompt share");
  return await response.json();
};

export const removePromptShare = async (authHeader: string, promptId: string, shareId: string): Promise<void> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  const response = await safeFetch(`${urlBase}/api/prompts/user/${promptId}/shares/${shareId}`, {
    method: "DELETE",
    headers: { Authorization: authHeader },
  });
  if (!response.ok) throw new Error("Failed to remove prompt share");
};

export const searchUserClauses = async (
  authHeader: string,
  params: SearchClausesParams = {},
): Promise<SearchClausesResponse> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  const query = new URLSearchParams();
  if (params.mode) query.set("mode", params.mode);
  if (params.q) query.set("q", params.q);
  if (params.similarTo) query.set("similarTo", params.similarTo);
  if (params.sort) query.set("sort", params.sort);
  if (params.offset != null) query.set("offset", String(params.offset));
  if (params.limit != null) query.set("limit", String(params.limit));
  for (const tag of params.tags ?? []) query.append("tags", tag);

  const response = await safeFetch(`${urlBase}/api/user-clauses?${query.toString()}`, {
    headers: { Authorization: authHeader },
  });
  if (!response.ok) throw new Error("Failed to search clauses");
  return response.json();
};

export const updateUserClause = async (
  authHeader: string,
  clauseId: string,
  updates: { tags?: string[]; contentText?: string },
): Promise<Clause> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  const response = await safeFetch(`${urlBase}/api/user-clauses/${clauseId}`, {
    method: "PATCH",
    headers: { Authorization: authHeader, "Content-Type": "application/json" },
    body: JSON.stringify(updates),
  });
  if (!response.ok) throw new Error("Failed to update clause");
  return response.json();
};

export const deleteUserClause = async (authHeader: string, clauseId: string): Promise<void> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  const response = await safeFetch(`${urlBase}/api/user-clauses/${clauseId}`, {
    method: "DELETE",
    headers: { Authorization: authHeader },
  });
  if (!response.ok) throw new Error("Failed to delete clause");
};

export const fetchUserClauseTags = async (authHeader: string): Promise<string[]> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  const response = await safeFetch(`${urlBase}/api/user-clauses/tags`, {
    headers: { Authorization: authHeader },
  });
  if (!response.ok) throw new Error("Failed to fetch clause tags");
  const data = await response.json();
  return data.tags ?? [];
};

export const getUserFiles = async (
  authHeader: string
): Promise<UserFile[]> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  const response = await safeFetch(`${urlBase}/api/files/user`, {
    method: "GET",
    headers: { Authorization: authHeader },
  });
  if (!response.ok) throw new Error("Failed to fetch user files");
  return z.array(UserFileSchema).parse(await response.json());
};

export const getManagedFiles = async (
  authHeader: string
): Promise<ManagedFile[]> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  const response = await safeFetch(`${urlBase}/api/files/managed`, {
    method: "GET",
    headers: { Authorization: authHeader },
  });
  if (!response.ok) throw new Error("Failed to fetch managed files");
  return await response.json();
};

export const createUserFile = async (
  authHeader: string,
  file: NewUserFile
): Promise<UserFile> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  const response = await safeFetch(`${urlBase}/api/files/user`, {
    method: "POST",
    body: JSON.stringify(file),
    headers: {
      Authorization: authHeader,
      'Content-Type': 'application/json'
    },
  });
  if (!response.ok) throw new Error("Failed to create user file");
  return await response.json();
};

export const deleteUserFile = async (
  authHeader: string,
  id: string
): Promise<void> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  const response = await safeFetch(`${urlBase}/api/files/user/${id}`, {
    method: "DELETE",
    headers: { Authorization: authHeader },
  });
  if (!response.ok) throw new Error("Failed to delete user file");
};

export const deleteManagedFile = async (
  authHeader: string,
  id: string
): Promise<void> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  const response = await safeFetch(`${urlBase}/api/files/managed/${id}`, {
    method: "DELETE",
    headers: { Authorization: authHeader },
  });
  if (!response.ok) throw new Error("Failed to delete managed file");
};

export const webAppFetchJson = (
  dioptraFetch: (url: string, options?: RequestInit) => Promise<unknown>
): FetchJson => (path) => dioptraFetch(`${getEnv("WEBAPP_API_BASE_URL")}${path}`, { method: "GET" });

export const downloadFile = async (
  dioptraFetch: (url: string, options?: RequestInit) => Promise<unknown>,
  storageUrl: string,
  filename: string
): Promise<void> => {
  await downloadStoredObject(webAppFetchJson(dioptraFetch), storageUrl, filename);
};

export const createConversationFile = async ({
  dioptraFetch,
  conversationId,
  fileName,
  fileBase64,
  fileUrl,
  inFocus,
}: {
  dioptraFetch: (url: string, options?: RequestInit) => Promise<any>;
  conversationId: string;
  fileName: string;
  fileBase64?: string;
  fileUrl?: string;
  inFocus: boolean;
}): Promise<{ attachment: AttachmentFile }> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  const data = await dioptraFetch(`${urlBase}/api/conversation-attachments-files`, {
    method: "POST",
    body: JSON.stringify({
      conversationId,
      fileName,
      fileBase64,
      fileUrl,
      inFocus,
    })
  });
  return z.object({ attachment: attachmentFileSchema }).passthrough().parse(data);
};

export const attachPromptFiles = async ({
  dioptraFetch,
  conversationId,
  promptId,
}: {
  dioptraFetch: (url: string, options?: RequestInit) => Promise<unknown>;
  conversationId: string;
  promptId: string;
}): Promise<void> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  await dioptraFetch(`${urlBase}/api/conversation-attachments-files/from-prompt`, {
    method: "POST",
    body: JSON.stringify({ conversationId, promptId }),
  });
};

export const updateConversationFileInFocus = async ({
  dioptraFetch,
  conversationId,
  fileName,
  fileBase64,
  fileUrl
}: {
  dioptraFetch: (url: string, options?: RequestInit) => Promise<any>;
  conversationId: string;
  fileName: string;
  fileBase64?: string;
  fileUrl?: string;
}): Promise<any> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  return await dioptraFetch(`${urlBase}/api/conversation-attachments-files`, {
    method: "POST",
    body: JSON.stringify({
      conversationId,
      fileName,
      fileBase64,
      fileUrl,
      inFocus: true
    })
  });
};

export const updateConversationFile = async (
  authHeader: string,
  fileId: string,
  fileName?: string,
  fileBase64?: string,
  inFocus?: boolean
): Promise<any> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  try {
    const response = await safeFetch(`${urlBase}/api/conversation-attachments-files/${fileId}`, {
      method: "PATCH",
      body: JSON.stringify({
        fileName,
        fileBase64,
        inFocus
      }),
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json'
      },
    });
    return await response.json();
  } catch (error) {
    throw new UserFacingError(
      "Failed to update conversation file",
      error instanceof Error ? error : new Error(String(error))
    );
  }
};

export const deleteConversationFile = async (
  dioptraFetch: (url: string, options?: RequestInit) => Promise<any>,
  fileId: string
): Promise<void> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  await dioptraFetch(`${urlBase}/api/conversation-attachments-files/${fileId}`, {
    method: "DELETE"
  });
};

export const getConversationFiles = async (
  dioptraFetch: (url: string, options?: RequestInit) => Promise<any>,
  conversationId: string
): Promise<AttachmentFile[]> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  const data = await dioptraFetch(`${urlBase}/api/conversation-attachments-files?conversationId=${conversationId}`, {
    method: "GET"
  });
  return z.object({ attachments: z.array(attachmentFileSchema).default([]) }).passthrough().parse(data).attachments;
};

// Full shape of the detail response: the DB row plus the parsed xlsx
// payload (header row -> `columns`, data rows -> `rows`).
export interface ConversationArtifactWithBody extends ConversationArtifact {
  columns?: string[];
  rows?: ConversationArtifactRow[];
}

export const getConversationArtifacts = async (
  dioptraFetch: (url: string, options?: RequestInit) => Promise<any>,
  conversationId: string
): Promise<ConversationArtifact[]> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  const data = await dioptraFetch(
    `${urlBase}/api/conversation-artifacts?conversationId=${encodeURIComponent(conversationId)}`,
    { method: "GET" }
  );
  return data.artifacts || [];
};

export const getConversationArtifact = async (
  dioptraFetch: (url: string, options?: RequestInit) => Promise<any>,
  artifactId: string
): Promise<ConversationArtifactWithBody | null> => {
  const urlBase = getEnv("API_BASE_URL");
  const data = await dioptraFetch(
    `${urlBase}/conversation-artifacts/${encodeURIComponent(artifactId)}`,
    { method: "GET" }
  );
  return data.artifact || null;
};

export const promoteConversationArtifact = async (
  dioptraFetch: (url: string, options?: RequestInit) => Promise<any>,
  artifactId: string
): Promise<ConversationArtifact> => {
  const urlBase = getEnv("API_BASE_URL");
  const data = await dioptraFetch(
    `${urlBase}/conversation-artifacts/${encodeURIComponent(artifactId)}/promote`,
    { method: "POST" }
  );
  return data.artifact;
};

export const deleteConversationArtifact = async (
  dioptraFetch: (url: string, options?: RequestInit) => Promise<any>,
  artifactId: string
): Promise<void> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  await dioptraFetch(
    `${urlBase}/api/conversation-artifacts/${encodeURIComponent(artifactId)}`,
    { method: "DELETE" }
  );
};

// Triggers a browser download of a table artifact as an xlsx, using the
// artifact's name for the filename. The backend returns a short-lived
// presigned URL with `Content-Disposition: attachment; filename=...` baked in
// — pointing the anchor directly at that URL lets the browser drop the file
// straight into the Downloads folder without showing a Save As dialog and
// without leaking the open Word document's name into the default suggestion
// (which is what the blob+createObjectURL path does inside the Word WebView).
//
// Calls the Flask backend directly via `API_BASE_URL` (already includes
// `/api/v1`); `dioptraFetch` attaches a fresh PropelAuth token, so a Next.js
// proxy would add nothing.
export const downloadConversationArtifact = async (
  dioptraFetch: (url: string, options?: RequestInit) => Promise<any>,
  artifactId: string
): Promise<void> => {
  const urlBase = getEnv("API_BASE_URL");
  const { url, filename } = PresignedDownloadResponseSchema.parse(
    await dioptraFetch(`${urlBase}/conversation-artifacts/${encodeURIComponent(artifactId)}/download`, { method: "GET" })
  );
  clickDownloadLink(url, filename);
};

export const createRule = async (
  authHeader: string,
  rule: Rule
): Promise<Rule> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");

  if (!rule.playbookId) {
    throw new Error("playbookId is required for rule creation");
  }

  const ruleResponse = await safeFetch(`${urlBase}/api/user-rules`, {
    method: "POST",
    body: JSON.stringify({
      name: rule.name,
      description: rule.description,
      type: rule.type,
      ruleType: rule.ruleType,
      rule: rule.rule,
      remedyType: rule.remedyType,
      remedy: rule.remedy,
      remedyModel: rule.remedyModel,
      comment: rule.comment,
      category: rule.category,
      importance: rule.importance,
      remedyName: rule.remedyName,
      remedyDescription: rule.remedyDescription,
      alternativeRemedyName: rule.alternativeRemedyName,
      alternativeRemedyDescription: rule.alternativeRemedyDescription,
      alternativeRemedy: rule.alternativeRemedy,
      alternativeRemedyContext: rule.alternativeRemedyContext,
      alternativeRemedyModel: rule.alternativeRemedyModel,
      alternativeRemedyTarget: rule.alternativeRemedyTarget,
    }),
    headers: {
      Authorization: authHeader,
      'Content-Type': 'application/json'
    },
  });
  if (!ruleResponse.ok) throw new Error("Failed to create rule");
  const newRule = await ruleResponse.json();

  const linkResponse = await safeFetch(`${urlBase}/api/playbook-rules`, {
    method: "POST",
    body: JSON.stringify({
      userPlaybookId: rule.playbookId,
      userRuleId: newRule.id
    }),
    headers: {
      Authorization: authHeader,
      'Content-Type': 'application/json'
    },
  });
  if (!linkResponse.ok) throw new Error("Failed to link rule to playbook");

  return newRule;
};

export const updateRule = async (
  authHeader: string,
  ruleId: string,
  rule: Rule
): Promise<Rule> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  const rulePath = rule.isManagedRule ? "managed-rules" : "user-rules";
  const response = await safeFetch(`${urlBase}/api/${rulePath}/${ruleId}`, {
    method: "PUT",
    body: JSON.stringify({
      name: rule.name,
      description: rule.description,
      type: rule.type,
      ruleType: rule.ruleType,
      rule: rule.rule,
      remedyType: rule.remedyType,
      remedy: rule.remedy,
      comment: rule.comment,
      category: rule.category,
      importance: rule.importance,
      remedyModel: rule.remedyModel,
      remedyName: rule.remedyName,
      remedyDescription: rule.remedyDescription,
      alternativeRemedyName: rule.alternativeRemedyName,
      alternativeRemedyDescription: rule.alternativeRemedyDescription,
      alternativeRemedy: rule.alternativeRemedy,
      alternativeRemedyContext: rule.alternativeRemedyContext,
      alternativeRemedyModel: rule.alternativeRemedyModel,
      alternativeRemedyTarget: rule.alternativeRemedyTarget,
    }),
    headers: {
      Authorization: authHeader,
      'Content-Type': 'application/json'
    },
  });
  if (!response.ok) throw new Error("Failed to update rule");

  return await response.json();
};

export const updateReview = async (
  authHeader: string,
  id: string,
  updateData: { concededRules?: string[]; respondedRules?: string[] }
): Promise<Review> => {
  try {
    const apiUrl = `${getEnv("WEBAPP_API_BASE_URL")}/api/reviews/${id}`;
    const response = await safeFetch(apiUrl, {
      method: "PATCH",
      headers: {
        Authorization: authHeader,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(updateData),
    });

    if (!response.ok) {
      throw new Error(`Failed to update review: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (err) {
    console.error("Failed to update review");
    console.error(err);
    throw err;
  }
};

export interface ConversationMemory {
  id: string;
  conversationId: string;
  contentText: string;
  contextText: string;
  propelAuthUserID: string;
  propelAuthOrgID: string;
  createdAt: string;
  tags?: {
    id: string;
    conversationMemoryId: string;
    tagId: string;
    propelAuthOrgID: string;
    createdAt: string;
    tag: {
      id: string;
      propelAuthOrgID: string;
      name: string;
      createdAt: string;
    };
  }[];
}

export interface NewConversationMemory {
  conversationId?: string;
  contentText: string;
  contextText: string;
}

export const getConversationMemories = async (
  authHeader: string,
  conversationId?: string
): Promise<ConversationMemory[]> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  const queryParams = conversationId ? `?conversationId=${conversationId}` : '';
  const response = await safeFetch(`${urlBase}/api/conversation-memories${queryParams}`, {
    method: "GET",
    headers: { Authorization: authHeader },
  });
  if (!response.ok) throw new Error("Failed to fetch conversation memories");

  const data = await response.json();
  return data.memories || [];
};

export const searchConversationMemories = async (
  authHeader: string,
  query: string,
  conversationId?: string
): Promise<ConversationMemory[]> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  const response = await safeFetch(`${urlBase}/api/conversation-memories/search`, {
    method: "POST",
    body: JSON.stringify({ query, conversationId }),
    headers: {
      Authorization: authHeader,
      'Content-Type': 'application/json'
    },
  });
  if (!response.ok) throw new Error("Failed to search conversation memories");

  const data = await response.json();
  return data.memories || [];
};

export const createConversationMemory = async (
  authHeader: string,
  memory: NewConversationMemory
): Promise<ConversationMemory> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  const response = await safeFetch(`${urlBase}/api/conversation-memories`, {
    method: "POST",
    body: JSON.stringify(memory),
    headers: {
      Authorization: authHeader,
      'Content-Type': 'application/json'
    },
  });
  if (!response.ok) throw new Error("Failed to create conversation memory");

  const data = await response.json();
  return data.memory;
};

export const updateConversationMemory = async (
  authHeader: string,
  id: string,
  updates: { contentText?: string; contextText?: string }
): Promise<ConversationMemory> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  const response = await safeFetch(`${urlBase}/api/conversation-memories/${id}`, {
    method: "PUT",
    body: JSON.stringify(updates),
    headers: {
      Authorization: authHeader,
      'Content-Type': 'application/json'
    },
  });
  if (!response.ok) throw new Error("Failed to update conversation memory");

  const data = await response.json();
  return data.memory;
};

export const deleteConversationMemory = async (
  authHeader: string,
  id: string
): Promise<void> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  const response = await safeFetch(`${urlBase}/api/conversation-memories/${id}`, {
    method: "DELETE",
    headers: { Authorization: authHeader },
  });
  if (!response.ok) throw new Error("Failed to delete conversation memory");
};

export const addMemoryTag = async (
  authHeader: string,
  memoryId: string,
  tagName: string
): Promise<{ tag: { id: string; name: string } }> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  const response = await safeFetch(`${urlBase}/api/conversation-memories/${memoryId}/tags`, {
    method: "POST",
    body: JSON.stringify({ tagName }),
    headers: {
      Authorization: authHeader,
      'Content-Type': 'application/json'
    },
  });
  if (!response.ok) throw new Error("Failed to add tag to memory");

  return await response.json();
};

export const removeMemoryTag = async (
  authHeader: string,
  memoryId: string,
  relationshipId: string
): Promise<void> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  const response = await safeFetch(`${urlBase}/api/conversation-memories/${memoryId}/tags/${relationshipId}`, {
    method: "DELETE",
    headers: { Authorization: authHeader },
  });
  if (!response.ok) throw new Error("Failed to remove tag from memory");
};

const icertisUploadDiagnosisSchema = z.discriminatedUnion("state", [
  z.object({ state: z.literal("not_connected") }),
  z.object({ state: z.literal("connection_expired") }),
  z.object({ state: z.literal("agreement_missing") }),
  z.object({ state: z.literal("agreement_forbidden") }),
  z.object({ state: z.literal("agreement_unreadable"), httpStatus: z.number() }),
  z.object({
    state: z.literal("agreement_found"),
    name: z.string().nullable(),
    businessStatus: z.string().nullable(),
    status: z.string().nullable(),
    isPublished: z.boolean().nullable(),
  }),
]);

export type IcertisUploadDiagnosis = z.infer<typeof icertisUploadDiagnosisSchema>;

export const diagnoseIcertisUpload = async (
  dioptraFetch: (url: string, options?: RequestInit) => Promise<any>,
  entityName: string,
  sysId: string
): Promise<IcertisUploadDiagnosis> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  const diagnosis = await dioptraFetch(
    `${urlBase}/api/integrations/icertis/agreements/${encodeURIComponent(entityName)}/${encodeURIComponent(sysId)}/diagnose-upload`,
    { method: "GET" }
  );
  return icertisUploadDiagnosisSchema.parse(diagnosis);
};
